import addChangeFundraiserDialogTemplate from './AddChangeFundraiserDialog.html';

/*****************************************************************************
 * Service: AddChangeFundraiserDialog */
/**
 * @ngdoc     service
 * @module    csm.books.fundraisers
 * @name      csm.books.fundraisers.service:AddChangeFundraiserDialog
 * @description
 * This service will display a dialog to manage a Fundraiser/Program. When
 * working with programs, the dialog will need to be passed references to
 * external functions that will be used for retrieving and saving programs. The
 * dialog resolves upon successful add/update. The Fundraiser/Program object is
 * passed as resolve data.
 *****************************************************************************/
/* @ngInject */ export function AddChangeFundraiserDialogService($q, $uibModal, FundRaiser, SERVER) {
  /*****************************************************************************
   * show */
  /**
   * @ngdoc    function
   * @name     show
   * @methodOf csm.books.fundraisers.service:AddChangeFundraiserDialog
   * @description
   * @param   {Number}     id            Fundraiser/Program ID to load. 0/NaN=New
   * @param   {Boolean}    isProgram     Indication that a program is being added
   *                                     or modified.
   * @param   {Array}      clubID        ID of club a new fundraiser will belong to.
   * @param   {Expression} getProgram    Reference to external function to use when
   *                                     loading a program. Will be passed an ID and
   *                                     expected to return the program object.
   * @param   {Expression} saveProgram   Reference to external function to use when
   *                                     saving a program. It will be passed a
   *                                     program object to save.
   * @return  {Promise}                  Resolves upon successful add/update of a
   *                                     fundraiser or program. The item object is
   *                                     passed as resolve data.
   *****************************************************************************/
  this.show = function (id, isProgram, clubID, getProgram, saveProgram) {
    return $uibModal.open({
      animation: false,
      backdrop: 'static',
      component: 'addChangeFundraiser',
      resolve: {
        isProgram: function () {
          return isProgram;
        },
        saveProgram: function () {
          return saveProgram;
        },
        fundraiser: function () {
          return $q.when(
            !!id
              ? isProgram
                ? getProgram({ id: id })
                : FundRaiser.get(id)
              : !isProgram
                ? {
                    id: 0,
                    active: true,
                    type: SERVER.BooksDBConstants.CT_FUNDRAISER,
                    clubID: clubID
                  }
                : {
                    id: 0,
                    active: true,
                    type: SERVER.BooksDBConstants.CT_PROGRAM
                  }
          );
        }
      }
    }).result;
  };
}

/*****************************************************************************
 * Component: addChangeFundraiser */
/**
 * @ngdoc     component
 * @module    csm.books.fundraisers
 * @name      csm.books.fundraisers.component:addChangeFundraiser
 * @description
 * This component will handle the management of Fundraiser/Program data. This
 * component is expected to be used with $uibModal.
 *****************************************************************************/
export const addChangeFundraiserComponent = {
  template: addChangeFundraiserDialogTemplate,
  bindings: { modalInstance: '<', resolve: '<' },
  controller: Controller
}

/*****************************************************************************
 * Controller: AddChangeFundraiser */
/**
 * @ngdoc     controller
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.controller:AddChangeFundraiser
 * @description
 * Controller for the addChangeFundraiser component.
 *****************************************************************************/
/* @ngInject */ function Controller($injector) {
  var $ctrl = this;
  $ctrl.validators = {};

  $ctrl.$onInit = $injector.invoke($onInit, null, { $ctrl: $ctrl });
  $ctrl.save = $injector.invoke(save, null, { $ctrl: $ctrl });
}

/*****************************************************************************
 * $onInit */
/**
 * @ngdoc     method
 * @name      $onInit
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.controller:addChangeFundraiser
 * @description
 * Called by angular when the component is ready. This will set up some
 * properties from the bound values.
 *****************************************************************************/
/* @ngInject */ function $onInit($ctrl) {
  return function () {
    /*-------------------------------------------------------*/
    /* Set the fundraiser/program model, flags and UI label. */
    /*-------------------------------------------------------*/
    $ctrl.fundraiser = $ctrl.resolve.fundraiser;
    $ctrl.isProgram = $ctrl.resolve.isProgram;
    $ctrl.isNew = !$ctrl.fundraiser.id;
    $ctrl.title =
      ($ctrl.isNew ? 'Add New ' : 'Update ') +
      ($ctrl.isProgram ? 'Program' : 'Fundraiser');

    /*-------------------------------------*/
    /* References to external service      */
    /* calls to handle program processing. */
    /*-------------------------------------*/
    $ctrl.saveProgram = $ctrl.resolve.saveProgram;

    /*---------------------------------------------------*/
    /* Programs use the `description` property for       */
    /* notes while fundraisers use the `notes` property. */
    /*---------------------------------------------------*/
    $ctrl.notes = $ctrl.isProgram
      ? $ctrl.fundraiser.description
      : $ctrl.fundraiser.notes;

    /*-----------------------------------*/
    /* Add passthrough to dismiss method */
    /*-----------------------------------*/
    $ctrl.dismiss = $ctrl.modalInstance.dismiss;

    /*-------------------------------------*/
    /* Flag to place focus on the first    */
    /* field after initilization is ready. */
    /*-------------------------------------*/
    $ctrl.nameHasFocus = true;
  };
}

/*****************************************************************************
 * save */
/**
 * @ngdoc     method
 * @name      save
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.controller:addChangeFundraiser
 * @description
 * Called when the Save button is clicked. This will validate the UI
 * before calling the save service. After successful save, the component
 * will call the dialog close passing in the fundraiser model.
 *****************************************************************************/
/* @ngInject */ function save($ctrl, Alert, BusyScreen, FundRaiser, Tools) {
  return function () {
    /*------------------*/
    /* Validate inputs. */
    /*------------------*/
    if (!$ctrl.validators.validate()) return;

    /*----------------------------------------------*/
    /* Verify start date is not after the end date. */
    /*----------------------------------------------*/
    if (
      !Tools.isNaNZero($ctrl.fundraiser.startDate) &&
      !Tools.isNaNZero($ctrl.fundraiser.endDate)
    )
      if ($ctrl.fundraiser.startDate > $ctrl.fundraiser.endDate) {
        Alert.error('Start date can not be after the end date');
        return;
      }

    /*------------------------------------------*/
    /* Add the notes to the fundraiser/program. */
    /*------------------------------------------*/
    if ($ctrl.isProgram) $ctrl.fundraiser.description = $ctrl.notes;
    else $ctrl.fundraiser.notes = $ctrl.notes;

    /*---------------------------------------------------*/
    /* Continue with save, using the correct save        */
    /* method for the fundraiser or program being saved. */
    /*---------------------------------------------------*/
    BusyScreen.promise(
      ($ctrl.isProgram ? $ctrl.saveProgram : FundRaiser.save)(
        $ctrl.fundraiser
      ).then(
        function (fundraiserID) {
          $ctrl.fundraiser.id = fundraiserID;
          $ctrl.modalInstance.close($ctrl.fundraiser);
        },
        function (resp) {
          Alert.error(' ', resp.parsedResponse.message());
        }
      )
    );
  };
}
